import {
  PERMANENT_UPGRADES,
  permanentUpgradeCost,
  type PermanentUpgradeId,
} from '../config/balance.js';
import type { UserProfile } from '../app/profile.js';
import { el, button } from './dom.js';

const BRANCHES: Record<string, PermanentUpgradeId[]> = {
  infrastructure: ['optimizedCabling', 'relayReserve', 'reinforcedCircuits'],
  tech: ['improvedTransmission', 'improvedBatteries'],
  resilience: ['magneticShielding', 'interferenceReduction'],
};

const BRANCH_META: Record<string, { label: string; hint: string }> = {
  infrastructure: {
    label: 'Infrastructure',
    hint: 'Plus de câbles et de relais pour bâtir votre réseau',
  },
  tech: {
    label: 'Technologie',
    hint: 'Pulses plus rapides et ampoules plus endurantes',
  },
  resilience: {
    label: 'Résilience',
    hint: 'Moins de malus sur zones magnétiques et croisements',
  },
};

const LABELS: Record<PermanentUpgradeId, string> = {
  optimizedCabling: 'Câblage optimisé',
  relayReserve: 'Réserve de relais',
  improvedTransmission: 'Transmission améliorée',
  reinforcedCircuits: 'Circuits renforcés',
  improvedBatteries: 'Batteries améliorées',
  magneticShielding: 'Bouclier magnétique',
  interferenceReduction: 'Anti-interférences',
};

const DESCRIPTIONS: Record<PermanentUpgradeId, string> = {
  optimizedCabling:
    'Chaque niveau augmente le stock de câbles en début de partie (+5 % par niveau).',
  relayReserve:
    'Chaque niveau ajoute 1 relais gratuit au départ (jusqu\'à 3). Les relais annulent l\'interférence aux croisements.',
  improvedTransmission:
    'Les impulsions circulent plus vite sur les câbles (+3 % de vitesse par niveau).',
  reinforcedCircuits:
    'Augmente le débit maximal des câbles (+20 % de capacité par niveau) pour moins d\'embouteillages.',
  improvedBatteries:
    'Les ampoules stockent plus d\'énergie (+3 % de capacité max par niveau) avant de s\'éteindre.',
  magneticShielding:
    'Réduit le ralentissement des pulses dans les zones magnétiques (−8 % de malus par niveau).',
  interferenceReduction:
    'Atténue la perte de débit quand des câbles se croisent sans relais (−8 % de malus par niveau).',
};

export function renderShop(
  container: HTMLElement,
  profile: UserProfile,
  onBuy: (id: PermanentUpgradeId, cost: number) => void,
  onBack: () => void,
): void {
  container.replaceChildren();

  const topBar = el('div', 'shop-topbar');
  topBar.append(
    button('← Retour', 'btn ghost shop-back-top', onBack),
    el('div', 'shop-top-meta', [
      el('h1', 'screen-title', ['Boutique']),
      el('p', 'fragments-display', [`${profile.fragments} fragments`]),
    ]),
  );
  container.append(topBar);

  const tabs = el('div', 'shop-tabs');
  let activeBranch = 'infrastructure';
  const branchHint = el('p', 'shop-branch-hint', [BRANCH_META.infrastructure!.hint]);
  const content = el('div', 'shop-grid');

  const renderItems = () => {
    content.replaceChildren();
    branchHint.textContent = BRANCH_META[activeBranch]?.hint ?? '';
    for (const id of BRANCHES[activeBranch] ?? []) {
      const def = PERMANENT_UPGRADES[id];
      const level = profile.upgrades[id] ?? 0;
      const maxed = level >= def.maxLevel;
      const nextLevel = level + 1;
      const cost = maxed ? 0 : permanentUpgradeCost(id, nextLevel);
      const card = el('div', 'shop-card');
      card.append(
        el('h3', '', [LABELS[id]]),
        el('p', 'shop-level', [`Niveau ${level} / ${def.maxLevel}`]),
        el('p', 'shop-desc', [DESCRIPTIONS[id]]),
      );
      if (!maxed) {
        const canAfford = profile.fragments >= cost;
        card.append(
          button(
            `${cost} fragments`,
            `btn primary ${canAfford ? '' : 'disabled'}`.trim(),
            () => {
              if (canAfford) onBuy(id, cost);
            },
          ),
        );
      } else {
        card.append(el('span', 'badge', ['MAX']));
      }
      content.append(card);
    }
  };

  for (const key of Object.keys(BRANCHES)) {
    const meta = BRANCH_META[key]!;
    const tab = button(
      meta.label,
      `shop-tab ${key === activeBranch ? 'active' : ''}`,
      () => {
        activeBranch = key;
        tabs.querySelectorAll('.shop-tab').forEach((t) => t.classList.remove('active'));
        tab.classList.add('active');
        renderItems();
      },
    );
    tabs.append(tab);
  }

  container.append(tabs, branchHint, content);
  renderItems();
}
